import React, { useState } from 'react';
import { X, AlertTriangle } from 'lucide-react';
import { motion } from 'framer-motion';

export default function DeleteConfirmModal({ board, onClose, onConfirm }) {
  const [confirmName, setConfirmName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const isMatch = confirmName.trim().toLowerCase() === board.name.toLowerCase();

  const handleSubmit = async () => {
    if (!isMatch) { setError('Board name does not match'); return; }
    setLoading(true);
    setError('');
    const res = await onConfirm(board.board_id, confirmName.trim());
    setLoading(false);
    if (res?.error) setError(res.error);
    else onClose();
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        onClick={e => e.stopPropagation()}
        className="w-full max-w-sm bg-bg-paper border border-red-500/20 rounded-2xl p-5 shadow-2xl"
        data-testid="delete-confirm-modal"
      >
        <div className="flex items-center gap-2 mb-4">
          <div className="w-8 h-8 rounded-full bg-red-500/10 flex items-center justify-center">
            <AlertTriangle className="w-4 h-4 text-red-400" />
          </div>
          <div>
            <h3 className="font-heading text-sm font-semibold text-red-400">Delete Board</h3>
            <p className="text-[10px] text-muted-foreground/60">This action moves the board to archive</p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-white/10 rounded-full ml-auto"><X className="w-4 h-4 text-muted-foreground" /></button>
        </div>

        <div className="bg-white/5 border border-white/10 rounded-xl p-3 mb-3">
          <p className="text-xs font-medium">{board.name}</p>
          <p className="text-[9px] font-mono text-muted-foreground/40">{board.serial}</p>
        </div>

        <p className="text-[11px] text-muted-foreground mb-2">
          Type <span className="text-white font-semibold">"{board.name}"</span> to confirm deletion:
        </p>
        <input
          data-testid="delete-confirm-input"
          type="text"
          value={confirmName}
          onChange={e => { setConfirmName(e.target.value); setError(''); }}
          placeholder="Type board name here..."
          className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-xs outline-none focus:border-red-500/50 placeholder:text-muted-foreground/40 mb-3"
          autoFocus
        />

        {error && <p data-testid="delete-error" className="text-[11px] text-red-400 mb-3">{error}</p>}

        <div className="flex gap-2">
          <button
            data-testid="delete-confirm-btn"
            onClick={handleSubmit}
            disabled={loading || !isMatch}
            className={`flex-1 text-xs font-bold py-2.5 rounded-xl transition-all ${isMatch ? 'bg-red-500 text-white hover:opacity-90' : 'bg-white/5 text-muted-foreground/40 cursor-not-allowed'} ${loading ? 'opacity-50' : ''}`}
          >
            {loading ? 'Deleting...' : 'Delete Board'}
          </button>
          <button onClick={onClose} className="px-4 bg-white/5 border border-white/10 text-muted-foreground text-xs py-2.5 rounded-xl hover:border-white/20">Cancel</button>
        </div>
      </motion.div>
    </motion.div>
  );
}
